import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../config/axios';
import conversationService from '../services/conversations';

const Dashboard = () => {
  // Navigation and authentication
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Core transformation state
  const [userInput, setUserInput] = useState('');
  
  // Check for prepopulated prompt from URL (cross-app integration)
  useEffect(() => {
    const prompt = searchParams.get('prompt');
    if (prompt) {
      setUserInput(decodeURIComponent(prompt));
      // Clear the URL param without reload
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [searchParams]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  
  // Enhanced features for conversation management
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [queryCategory, setQueryCategory] = useState('general');
  const [showHistory, setShowHistory] = useState(false);
  const [editingConvId, setEditingConvId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [latestEmitMessageId, setLatestEmitMessageId] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const latestEmitMessageRef = useRef(null);

  // Category definitions with icons and descriptions
  const categories = [
    { 
      value: 'general', 
      label: '💬 General Guidance', 
      icon: '💬',
      description: 'Open dialogue about any topic'
    },
    { 
      value: 'biblical', 
      label: '📖 Biblical Study', 
      icon: '📖',
      description: 'Scripture analysis through Fhoism lens (9-section format)'
    },
    { 
      value: 'self-development', 
      label: '🌱 Self-Development', 
      icon: '🌱',
      description: 'Personal growth & transformation steps'
    },
    { 
      value: 'mindstreams', 
      label: '🧠 MindStreams', 
      icon: '🧠',
      description: 'Power, Flow & Clarity practices'
    }
  ];

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setSidebarOpen(false); // Close mobile sidebar on desktop
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle swipe gestures
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isRightSwipe && !sidebarOpen) {
      setSidebarOpen(true);
    }
    if (isLeftSwipe && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  // Generate unique ID for conversations
  const generateId = () => {
    return 'temp_' + Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  // Save conversation to server
  const saveConversationToServer = async (conversation) => {
    // Don't save empty conversations
    if (!conversation.messages || conversation.messages.length === 0) {
      console.log('Not saving empty conversation');
      return;
    }
    
    try {
      // If conversation has no ID or it's a temp ID, create it
      if (!conversation.id || conversation.id.includes('temp_')) {
        const response = await conversationService.createConversation({
          title: conversation.title,
          messages: conversation.messages,
          category: conversation.category || 'general',
          created_at: new Date(conversation.createdAt || Date.now()).toISOString(),
          updated_at: new Date(conversation.updatedAt || Date.now()).toISOString()
        });
        
        // Update local conversation with backend ID
        if (response && response.id) {
          setConversations(prevConversations => 
            prevConversations.map(conv => 
              conv.id === conversation.id ? { ...conv, id: response.id } : conv
            )
          );
          if (currentConversationId === conversation.id) {
            setCurrentConversationId(response.id);
          }
        }
      } else {
        // Existing conversation - update it
        await conversationService.updateConversation(conversation.id, {
          title: conversation.title,
          messages: conversation.messages,
          updated_at: new Date(conversation.updatedAt || Date.now()).toISOString()
        });
      }
    } catch (error) {
      console.error('Error saving conversation to server:', error);
    }
  };

  // Scroll to bottom of messages (for user messages)
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Scroll to top of Emit's response
  const scrollToEmitResponse = () => {
    if (latestEmitMessageRef.current) {
      latestEmitMessageRef.current.scrollIntoView({ 
        behavior: "smooth", 
        block: "start" 
      });
    }
  };
  
  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  // Create new conversation
  const startNewConversation = () => {
    const newConv = {
      id: generateId(),
      title: `New Conversation`,
      messages: [],
      category: queryCategory,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    setConversations([...conversations, newConv]);
    setCurrentConversationId(newConv.id);
    setUserInput('');
    setError('');
    // Auto-start editing the title
    setEditingConvId(newConv.id);
    setEditingTitle(newConv.title);
    
    // Close mobile sidebar after creating new conversation
    if (isMobile) {
      setSidebarOpen(false);
    }
    
    // Don't save to server until first message is sent
  };

  // Rename conversation
  const renameConversation = async (id, newTitle) => {
    setConversations(prevConversations => 
      prevConversations.map(conv => 
        conv.id === id ? { ...conv, title: newTitle || 'Untitled', updatedAt: Date.now() } : conv
      )
    );
    setEditingConvId(null);
    setEditingTitle('');
    
    // Only update on server if it's not a temp conversation
    if (!id.includes('temp_')) {
      try {
        await conversationService.updateConversation(id, { 
          title: newTitle || 'Untitled',
          updated_at: new Date(Date.now()).toISOString()
        });
      } catch (error) {
        console.error('Error updating conversation title:', error);
      }
    }
  };

  // Auto-generate title from first message
  const generateTitleFromMessage = (message) => {
    const words = message.split(' ');
    return words.slice(0, 5).join(' ') + (words.length > 5 ? '...' : '');
  };

  // Get category icon
  const getCategoryIcon = (categoryValue) => {
    const category = categories.find(c => c.value === categoryValue);
    return category ? category.icon : '💬';
  };

  // Scroll effect for new Emit messages
  useEffect(() => {
    if (latestEmitMessageId) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        scrollToEmitResponse();
      }, 100);
    }
  }, [latestEmitMessageId]);
  
  // Load conversations from localStorage and server
  useEffect(() => {
    const loadConversations = async () => {
      const savedLocal = localStorage.getItem('emitConversations');
      let localConversations = [];
      
      if (savedLocal) {
        try {
          localConversations = JSON.parse(savedLocal);
        } catch (e) {
          console.error('Error parsing local conversations:', e);
        }
      }
      
      // Load and merge with server conversations
      try {
        const serverConversations = await conversationService.getConversations();
        
        // Create a map for easy lookup
        const localMap = new Map(localConversations.map(c => [c.id, c]));
        const serverMap = new Map(serverConversations.map(c => [c.id, c]));
        
        // Merge conversations (prefer newer version)
        const merged = [];
        const processedIds = new Set();
        
        // Process all conversations
        [...localConversations, ...serverConversations].forEach(conv => {
          if (!processedIds.has(conv.id)) {
            processedIds.add(conv.id);
            const localConv = localMap.get(conv.id);
            const serverConv = serverMap.get(conv.id);
            
            if (localConv && serverConv) {
              // Both exist - use newer one
              merged.push(localConv.updatedAt > serverConv.updated_at ? localConv : serverConv);
            } else {
              // Only one exists
              merged.push(conv);
            }
          }
        });
        
        setConversations(merged);
        
        if (merged.length > 0) {
          setCurrentConversationId(merged[merged.length - 1].id);
        }
      } catch (error) {
        console.error('Error loading conversations:', error);
        // Fallback to local only
        setConversations(localConversations);
        if (localConversations.length > 0) {
          setCurrentConversationId(localConversations[localConversations.length - 1].id);
        }
      }
    };
    
    loadConversations();
  }, []);
  
  // Save conversations to localStorage and server when updated
  useEffect(() => {
    if (conversations.length > 0) {
      // Save to localStorage
      localStorage.setItem('emitConversations', JSON.stringify(conversations));
      
      // Save current conversation to server (debounced)
      const currentConv = conversations.find(c => c.id === currentConversationId);
      if (currentConv && currentConv.messages.length > 0) {
        const timer = setTimeout(() => {
          saveConversationToServer(currentConv);
        }, 1000); // Debounce by 1 second
        
        return () => clearTimeout(timer);
      }
    }
  }, [conversations, currentConversationId]);
  
  // Check authentication on load
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    const fetchUserData = async () => {
      try {
        const response = await axios.get('/users/me');
        setUserData(response.data);
      } catch (err) {
        console.error('Error fetching user data:', err);
        if (err.response && err.response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserData();
  }, [navigate]);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('emitConversations');
    navigate('/login');
  };

  // Function to clean system tags from the response
  const cleanSystemTags = (text) => {
    if (!text) return '';
    
    const tagsToRemove = [
      'userStyle', 'search_reminders', 'automated_reminder_from_anthropic',
      'citation_instructions', 'web_search_tool', 'research_process',
      'research_is_active', 'functions', 'critical_reminders',
      'artifacts_info', 'election_info', 'harmful_content_safety',
      'web_search_examples', 'styles_info'
    ];
    
    let cleaned = text;
    tagsToRemove.forEach(tag => {
      const pattern = new RegExp(`<${tag}>[\\s\\S]*?<\\/${tag}>`, 'g');
      cleaned = cleaned.replace(pattern, '');
    });
    
    return cleaned.trim();
  };

  // Get current conversation
  const getCurrentConversation = () => {
    return conversations.find(conv => conv.id === currentConversationId);
  };

  // Delete conversation
  const deleteConversation = async (id) => {
    const filtered = conversations.filter(conv => conv.id !== id);
    setConversations(filtered);
    if (id === currentConversationId) {
      setCurrentConversationId(filtered.length > 0 ? filtered[filtered.length - 1].id : null);
    }
    
    // Delete from server only if it's not a temp conversation
    if (!id.includes('temp_')) {
      try {
        await conversationService.deleteConversation(id);
      } catch (error) {
        console.error('Error deleting from server:', error);
      }
    }
  };

  // Handle conversation selection
  const selectConversation = (id) => {
    setCurrentConversationId(id);
    // Close mobile sidebar after selection
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Process user input with Emit
  const handleSubmit = async () => {
    // Prevent multiple submissions
    if (isProcessing) {
      return;
    }
    
    if (!userInput.trim()) {
      setError('Please enter a question or topic');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    
    let activeConvId = currentConversationId;
    
    // Create new conversation if none exists
    if (!activeConvId) {
      const newConv = {
        id: generateId(),
        title: generateTitleFromMessage(userInput),
        messages: [],
        category: queryCategory,
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      const newConversations = [...conversations, newConv];
      setConversations(newConversations);
      setCurrentConversationId(newConv.id);
      activeConvId = newConv.id;
      
      // Don't save empty conversation to server yet
    }
    
    // Create user message
    const userMessage = {
      id: generateId(),
      role: 'user',
      content: userInput,
      category: queryCategory,
      timestamp: Date.now()
    };
    
    try {
      // Update conversations with user message
      setConversations(prevConversations => {
        const updated = prevConversations.map(conv => {
          if (conv.id === activeConvId) {
            // Auto-update title if it's the first message
            const updatedConv = {
              ...conv,
              messages: [...conv.messages, userMessage],
              updatedAt: Date.now()
            };
            // If this is the first message and title is still default, update it
            if (conv.messages.length === 0 && conv.title === 'New Conversation') {
              updatedConv.title = generateTitleFromMessage(userInput);
            }
            return updatedConv;
          }
          return conv;
        });
        
        // CARMACK FIX: If conversation not found, create it with user message
        if (!updated.find(c => c.id === activeConvId)) {
          console.log('Creating conversation with user message');
          updated.push({
            id: activeConvId,
            title: generateTitleFromMessage(userInput),
            messages: [userMessage],
            category: queryCategory,
            createdAt: Date.now(),
            updatedAt: Date.now()
          });
        }
        
        return updated;
      });
      
      setUserInput('');
      
      // Scroll to bottom after user message
      setTimeout(scrollToBottom, 100);
      
      // Get the current conversation for message history
      const currentConv = conversations.find(c => c.id === activeConvId);
      const messageHistory = currentConv ? [...currentConv.messages, userMessage].slice(-10) : [userMessage];
      
      console.log('Sending request to Emit with context:', {
        prompt: userMessage.content,
        category: queryCategory,
        conversation_id: activeConvId,
        message_count: messageHistory.length
      });
      
      // Make API request with conversation context
      const response = await axios.post('/claude-direct', {
        prompt: userMessage.content,
        category: queryCategory,
        conversation_id: activeConvId,
        message_history: messageHistory
      });
      
      console.log('Response received from Emit:', response.data);
      
      if (response.data && response.data.completion) {
        // Clean any system tags from the response
        const cleanedResponse = cleanSystemTags(response.data.completion);
        
        // Create Emit's response message
        const emitMessage = {
          id: generateId(),
          role: 'assistant',
          content: cleanedResponse,
          timestamp: Date.now(),
          tokensUsed: response.data.usage?.total_tokens || 0
        };
        
        // Set the latest Emit message ID for scrolling
        setLatestEmitMessageId(emitMessage.id);
        
        // Update conversations with Emit's response using callback to ensure fresh state
        setConversations(prevConversations => {
          console.log('BEFORE UPDATE:', {
            convCount: prevConversations.length,
            activeConvId,
            conversations: prevConversations.map(c => ({id: c.id, messages: c.messages.length}))
          });
          
          // FORCE UPDATE - Nuclear option
          let foundConv = false;
          const updated = prevConversations.map(conv => {
            console.log('Checking conv:', conv.id, 'against activeConvId:', activeConvId, 'Match?', conv.id === activeConvId);
            if (conv.id === activeConvId) {
              foundConv = true;
              console.log('UPDATING CONVERSATION:', conv.id);
              const newConv = {
                ...conv,
                messages: [...(conv.messages || []), emitMessage],
                updatedAt: Date.now()
              };
              console.log('NEW MESSAGE COUNT:', newConv.messages.length);
              
              // Save to server after response
              setTimeout(() => saveConversationToServer(newConv), 500);
              return newConv;
            }
            return conv;
          });
          
          // If conversation not found, create it!
          if (!foundConv && activeConvId.includes('temp_')) {
            console.log('CONVERSATION NOT FOUND! Creating it with messages');
            
            // Get the user message that should already be there
            const existingConv = prevConversations.find(c => c.id === activeConvId);
            const existingMessages = existingConv ? [...existingConv.messages] : [];
            
            // If no existing messages, reconstruct from messageHistory
            if (existingMessages.length === 0) {
              console.log('No existing messages, using messageHistory');
              existingMessages.push(userMessage);
            }
            
            const newConv = {
              id: activeConvId,
              title: existingConv?.title || generateTitleFromMessage(userMessage.content),
              messages: [...existingMessages, emitMessage],
              category: queryCategory,
              createdAt: existingConv?.createdAt || Date.now(),
              updatedAt: Date.now()
            };
            
            updated.push(newConv);
            
            // Save to server after creation
            setTimeout(() => saveConversationToServer(newConv), 500);
          }
          
          console.log('AFTER UPDATE:', updated.map(c => ({id: c.id, messages: c.messages.length})));
          return updated;
        });
        
        // Update user token count if provided
        if (response.data.remaining_tokens !== undefined) {
          setUserData(prev => ({ ...prev, tokens: response.data.remaining_tokens }));
        }
      } else {
        throw new Error('No response received from Emit');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(`Error: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  // Loading screen
  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}>Loading...</div>
      </div>
    );
  }

  const currentConv = getCurrentConversation();
  const currentCategory = categories.find(c => c.value === queryCategory);

  // Main render
  return (
    <div style={styles.container}
         onTouchStart={isMobile ? handleTouchStart : undefined}
         onTouchMove={isMobile ? handleTouchMove : undefined}
         onTouchEnd={isMobile ? handleTouchEnd : undefined}>
      <header style={{...styles.header, ...(isMobile ? mobileStyles.header : {})}}>
        <div style={{...styles.headerLeft, ...(isMobile ? mobileStyles.headerLeft : {})}}>
          <h1 style={{...styles.title, ...(isMobile ? mobileStyles.title : {})}}>Emit: Fhoism Philosophical Guide</h1>
          <p style={styles.subtitle}>Your Bridge to Freedom, Happiness, and Oneness</p>
        </div>
        <div style={{...styles.headerRight, ...(isMobile ? mobileStyles.headerRight : {})}}>
          <div style={{...styles.userInfo, ...(isMobile ? mobileStyles.userInfo : {})}}>
            <p>Welcome, {userData?.full_name || 'User'}</p>
            <p style={styles.tokens}>Tokens: {userData?.tokens || 0}</p>
            {isSyncing && <p style={styles.syncStatus}>Syncing...</p>}
          </div>
          <button onClick={handleLogout} style={styles.logoutBtn}>Logout</button>
        </div>
      </header>
      
      {/* Mobile Menu Button */}
      {isMobile && (
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: 'fixed',
            left: '10px',
            top: '80px',
            zIndex: 1200,
            background: '#0d6efd',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            padding: '10px',
            cursor: 'pointer',
            fontSize: '20px',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
          }}
        >
          ☰
        </button>
      )}
      
      {/* Desktop Sidebar Toggle Button - FIXED VERSION */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        style={{
          position: 'fixed',
          left: sidebarCollapsed ? '10px' : '310px',
          top: '100px',
          zIndex: 1001,
          background: '#0d6efd',
          color: 'white',
          border: '2px solid white',
          borderRadius: '0 4px 4px 0',
          padding: '8px 4px',
          cursor: 'pointer',
          transition: 'left 0.3s ease',
          fontSize: '20px',
          width: '35px',
          height: '45px',
          display: isMobile ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 0 10px rgba(13, 110, 253, 0.5)'
        }}
        title={sidebarCollapsed ? 'Show Conversations' : 'Hide Conversations'}
      >
        {sidebarCollapsed ? '▶' : '◀'}
      </button>
      
      {/* Overlay for mobile */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1099
          }}
        />
      )}
      
      <main style={styles.main}>
        <div 
          style={{
            ...styles.sidebar, 
            ...(isMobile ? {
              ...mobileStyles.sidebar,
              ...(sidebarOpen ? mobileStyles.sidebarOpen : {})
            } : {
              width: sidebarCollapsed ? "0" : "300px", 
              overflow: "hidden", 
              transition: "width 0.3s ease"
            })
          }}
        >
          <button 
            onClick={startNewConversation} 
            style={styles.newConvBtn}
          >
            + New Conversation
          </button>
          <div style={styles.convList}>
            <h3>Recent Conversations</h3>
            {conversations.slice().reverse().map(conv => (
              <div 
                key={conv.id}
                style={{
                  ...styles.convItem,
                  ...(conv.id === currentConversationId ? styles.activeConv : {})
                }}
                onClick={() => selectConversation(conv.id)}
              >
                {editingConvId === conv.id ? (
                  <input
                    type="text"
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.target.value)}
                    onBlur={() => renameConversation(conv.id, editingTitle)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        renameConversation(conv.id, editingTitle);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    style={styles.titleInput}
                    autoFocus
                  />
                ) : (
                  <div 
                    onDoubleClick={() => {
                      setEditingConvId(conv.id);
                      setEditingTitle(conv.title);
                    }}
                    style={styles.convTitle}
                  >
                    <span>{getCategoryIcon(conv.category)}</span> {conv.title}
                  </div>
                )}
                <div style={styles.convMeta}>
                  {conv.messages?.length || 0} messages
                </div>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteConversation(conv.id);
                  }}
                  style={styles.deleteBtn}
                  title="Delete conversation"
                >
                  ×
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingConvId(conv.id);
                    setEditingTitle(conv.title);
                  }}
                  style={styles.editBtn}
                  title="Rename conversation"
                >
                  ✏️
                </button>
              </div>
            ))}
          </div>
        </div>
        
        <div style={{
          ...styles.chatArea, 
          ...(isMobile ? mobileStyles.chatArea : {
            marginLeft: sidebarCollapsed ? "0" : "0", 
            width: sidebarCollapsed ? "100%" : "calc(100% - 300px)", 
            transition: "width 0.3s ease"
          })
        }}>
          <div style={{...styles.messagesContainer, overflowY: currentConv ? "auto" : "visible"}}>
            {currentConv ? (
              <>
                {console.log("RENDERING MESSAGES:", currentConv.messages?.length || 0, "messages")}
                {(currentConv.messages || []).map(msg => (
                  <div 
                    key={msg.id}
                    ref={msg.id === latestEmitMessageId && msg.role === 'assistant' ? latestEmitMessageRef : null}
                    style={{
                      ...styles.message,
                      ...(msg.role === 'user' ? styles.userMessage : styles.emitMessage),
                      ...(isMobile ? {
                        ...mobileStyles.message,
                        ...(msg.role === 'user' ? mobileStyles.userMessage : mobileStyles.emitMessage)
                      } : {})
                    }}
                  >
                    <div style={styles.messageHeader}>
                      <strong>{msg.role === 'user' ? 'You' : 'Emit'}</strong>
                      <span style={styles.timestamp}>
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div style={{...styles.messageContent, ...(isMobile ? mobileStyles.messageContent : {})}}>
                      {msg.content.split('\n').map((line, index) => (
                        <p key={index} style={styles.messagePara}>{line || ' '}</p>
                      ))}
                    </div>
                  </div>
                ))}
                {isProcessing && (
                  <div style={styles.processingMessage}>
                    <div style={styles.thoughtBubbleContainer}>
                      <div style={styles.thoughtBubble}>
                        <div style={styles.typingIndicator}>
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                      <div style={styles.thoughtBubbleSmall}></div>
                      <div style={styles.thoughtBubbleTiny}></div>
                    </div>
                    <div style={styles.contemplatingText}>
                      Emit is contemplating your {currentCategory?.label.replace(currentCategory?.icon, '').trim()} question...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            ) : (
              <div style={styles.welcomeMessage}>
                <h2 style={{marginTop: 0, marginBottom: '8px'}}>Welcome to Emit</h2>
                <p style={{fontSize: '14px', marginBottom: '10px'}}>Start a new conversation below to explore Fhoism wisdom</p>
                
                <div style={{fontSize: '12px', color: '#6c757d'}}>
                  <p style={{marginBottom: '5px', fontWeight: 'bold'}}>Try asking about:</p>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '4px', maxWidth: '400px', margin: '0 auto'}}>
                    <div>📖 Scripture (e.g., "Genesis 1-4")</div>
                    <div>🧠 Three MindStreams</div>
                    <div>🙏 Daily practices</div>
                    <div>💭 Fhoism teachings</div>
                  </div>
                  <p style={{marginTop: '6px', fontSize: '11px', fontStyle: 'italic'}}>
                    💡 Use Response Format buttons below
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div style={styles.inputArea}>
            <div style={styles.categoryContainer}>
              <label style={styles.categoryLabel}>Response Format:</label>
              <div style={{...styles.categoryButtons, ...(isMobile ? mobileStyles.categoryButtons : {})}}>
                {categories.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setQueryCategory(cat.value)}
                    style={{
                      ...styles.categoryButton,
                      ...(queryCategory === cat.value ? styles.categoryButtonActive : {})
                    }}
                    title={cat.description}
                  >
                    <span style={styles.categoryIcon}>{cat.icon}</span>
                    <span style={styles.categoryText}>{cat.label.replace(cat.icon, '').trim()}</span>
                  </button>
                ))}
              </div>
              {currentCategory && (
                <div style={styles.categoryDescription}>
                  {currentCategory.description}
                </div>
              )}
            </div>
            
            {error && (
              <div style={styles.errorBox}>
                {error}
              </div>
            )}
            
            <div style={{...styles.inputContainer, ...(isMobile ? mobileStyles.inputContainer : {})}}>
              <textarea
                ref={textareaRef}
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }}
                placeholder="Enter your question or topic (e.g., 'Genesis 1-4', 'Understanding the Three MindStreams', 'How can I develop my morning practice?')"
                style={styles.textarea}
                disabled={isProcessing}
              />
              <button
                onClick={handleSubmit}
                disabled={isProcessing || !userInput.trim()}
                style={{
                  ...styles.submitBtn,
                  ...(isProcessing || !userInput.trim() ? styles.submitBtnDisabled : {}),
                  ...(isMobile ? mobileStyles.submitBtn : {})
                }}
              >
                {isProcessing ? 'Processing...' : 'Submit to Emit'}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const styles = {
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#f8f9fa',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    overflow: 'hidden',
    position: 'relative'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh'
  },
  loadingSpinner: {
    fontSize: '24px',
    color: '#0d6efd'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: 'white',
    borderBottom: '3px solid #0d6efd',
    boxShadow: '0 2px 8px rgba(13, 110, 253, 0.1)'
  },
  headerLeft: {
    flex: 1
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  },
  title: {
    margin: 0,
    fontSize: '28px',
    color: '#0d6efd',
    fontWeight: '600'
  },
  subtitle: {
    margin: '5px 0 0 0',
    color: '#6c757d',
    fontSize: '14px'
  },
  userInfo: {
    textAlign: 'right'
  },
  tokens: {
    fontWeight: 'bold',
    color: '#28a745',
    fontSize: '16px'
  },
  syncStatus: {
    fontSize: '12px',
    color: '#28a745',
    fontStyle: 'italic'
  },
  logoutBtn: {
    padding: '8px 16px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#c82333'
    }
  },
  main: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    height: '100%',
    position: 'relative'
  },
  sidebar: {
    width: '300px',
    backgroundColor: 'white',
    borderRight: '1px solid #dee2e6',
    padding: '20px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden'
  },
  newConvBtn: {
    width: '100%',
    padding: '12px',
    backgroundColor: '#0d6efd',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    marginBottom: '20px',
    fontWeight: '500',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#0b5ed7'
    }
  },
  syncBtn: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    marginBottom: '20px',
    fontWeight: '500',
    transition: 'all 0.2s'
  },
  convList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    flex: '1 1 auto',
    overflowY: 'scroll',  // Force scroll, not auto
    overflowX: 'hidden',
    minHeight: '0',
    paddingRight: '10px',
    position: 'relative',
    scrollbarWidth: 'thin',  // Firefox
    scrollbarColor: '#0d6efd #f1f1f1'  // Firefox
  },
  convItem: {
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    cursor: 'pointer',
    position: 'relative',
    border: '1px solid transparent',
    transition: 'all 0.2s'
  },
  activeConv: {
    backgroundColor: '#e7f1ff',
    border: '1px solid #0d6efd'
  },
  convTitle: {
    fontWeight: '500',
    marginBottom: '4px',
    cursor: 'text',
    color: '#212529'
  },
  titleInput: {
    width: '100%',
    padding: '4px',
    border: '2px solid #0d6efd',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500'
  },
  convMeta: {
    fontSize: '12px',
    color: '#6c757d',
    marginTop: '4px'
  },
  deleteBtn: {
    position: 'absolute',
    top: '5px',
    right: '5px',
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#dc3545',
    opacity: 0.7,
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 1
    }
  },
  editBtn: {
    position: 'absolute',
    top: '5px',
    right: '30px',
    background: 'none',
    border: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    opacity: 0.6,
    transition: 'opacity 0.2s',
    '&:hover': {
      opacity: 1
    }
  },
  chatArea: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    minHeight: 0  // Prevent container from forcing minimum height
  },
  welcomeMessage: {
    textAlign: 'center',
    paddingTop: '20px',  // Move content toward top
    color: '#6c757d'
  },
  message: {
    marginBottom: '20px',
    padding: '15px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  userMessage: {
    backgroundColor: '#e7f1ff',
    marginLeft: '20%',
    borderLeft: '3px solid #0d6efd'
  },
  emitMessage: {
    backgroundColor: '#f0fdf4',
    marginRight: '20%',
    borderLeft: '3px solid #28a745'
  },
  messageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px'
  },
  timestamp: {
    fontSize: '12px',
    color: '#6c757d'
  },
  messageContent: {
    lineHeight: '1.6'
  },
  messagePara: {
    margin: '0.5em 0'
  },
  processingMessage: {
    textAlign: 'center',
    color: '#6c757d',
    padding: '20px'
  },
  typingIndicator: {
    display: 'inline-flex',
    gap: '4px',
    marginBottom: '10px'
  },
  thoughtBubbleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: '15px'
  },
  thoughtBubble: {
    background: 'white',
    border: '3px solid #e0e0e0',
    borderRadius: '50%',
    padding: '20px',
    width: '100px',
    height: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    animation: 'float 3s ease-in-out infinite',
    boxShadow: '0 5px 15px rgba(0,0,0,0.1)'
  },
  thoughtBubbleSmall: {
    background: 'white',
    border: '3px solid #e0e0e0',
    borderRadius: '50%',
    width: '30px',
    height: '30px',
    marginTop: '-10px',
    marginLeft: '50px',
    animation: 'float 3s ease-in-out infinite 0.5s'
  },
  thoughtBubbleTiny: {
    background: 'white',
    border: '3px solid #e0e0e0',
    borderRadius: '50%',
    width: '15px',
    height: '15px',
    marginTop: '-5px',
    marginLeft: '70px',
    animation: 'float 3s ease-in-out infinite 1s'
  },
  contemplatingText: {
    textAlign: 'center',
    color: '#6c757d',
    fontSize: '16px',
    marginTop: '10px'
  },

  inputArea: {
    backgroundColor: 'white',
    borderTop: '2px solid #0d6efd',
    padding: '20px',
    boxShadow: '0 -2px 8px rgba(13, 110, 253, 0.1)'
  },
  categoryContainer: {
    marginBottom: '15px'
  },
  categoryLabel: {
    display: 'block',
    marginBottom: '8px',
    color: '#495057',
    fontWeight: '500'
  },
  categoryButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  categoryButton: {
    padding: '8px 16px',
    border: '2px solid #0d6efd',
    backgroundColor: 'white',
    color: '#0d6efd',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.2s',
    fontWeight: '500'
  },
  categoryButtonActive: {
    backgroundColor: '#0d6efd',
    color: 'white'
  },
  categoryIcon: {
    fontSize: '18px'
  },
  categoryText: {
    fontSize: '14px'
  },
  categoryDescription: {
    marginTop: '8px',
    fontSize: '13px',
    color: '#6c757d',
    fontStyle: 'italic'
  },
  errorBox: {
    padding: '10px',
    marginBottom: '15px',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '4px',
    color: '#721c24'
  },
  inputContainer: {
    display: 'flex',
    gap: '10px'
  },
  textarea: {
    flex: 1,
    padding: '12px',
    borderRadius: '8px',
    border: '2px solid #ced4da',
    resize: 'vertical',
    minHeight: '100px',
    fontFamily: 'inherit',
    fontSize: '14px',
    transition: 'border-color 0.2s',
    '&:focus': {
      borderColor: '#0d6efd',
      outline: 'none'
    }
  },
  submitBtn: {
    padding: '12px 24px',
    backgroundColor: '#0d6efd',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    alignSelf: 'flex-end',
    fontWeight: '500',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: '#0b5ed7'
    }
  },
  submitBtnDisabled: {
    backgroundColor: '#6c757d',
    cursor: 'not-allowed'
  }
};

// Mobile-specific styles
const mobileStyles = {
  header: {
    flexDirection: 'column',
    padding: '15px',
    gap: '10px'
  },
  headerLeft: {
    textAlign: 'center',
    width: '100%'
  },
  headerRight: {
    width: '100%',
    justifyContent: 'space-between',
    flexDirection: 'row'
  },
  title: {
    fontSize: '24px'
  },
  userInfo: {
    fontSize: '14px',
    textAlign: 'left'
  },
  sidebar: {
    width: '300px',
    backgroundColor: 'white',
    borderRight: '1px solid #dee2e6',
    padding: '20px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden'
  },
  sidebarOpen: {
    transform: 'translateX(0)'
  },
  chatArea: {
    width: '100%',
    marginLeft: '0'
  },
  message: {
    margin: '10px 0',
    padding: '12px'
  },
  userMessage: {
    marginLeft: '10%'
  },
  emitMessage: {
    marginRight: '10%'
  },
  messageContent: {
    fontSize: '14px'
  },
  inputContainer: {
    flexDirection: 'column',
    gap: '10px'
  },
  submitBtn: {
    width: '100%',
    alignSelf: 'stretch'
  },
  categoryButtons: {
    gridTemplateColumns: 'repeat(2, 1fr)',
    display: 'grid',
    gap: '8px'
  }
};

// Add CSS animation for visibility
if (typeof document !== 'undefined' && !document.getElementById('dashboard-animations')) {
  const style = document.createElement('style');
  style.id = 'dashboard-animations';
  style.textContent = `
    @keyframes float {
      0% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
      100% { transform: translateY(0px); }
    }
  `;
  document.head.appendChild(style);
}

export default Dashboard;
